import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { FaCheck, FaCreditCard, FaPaypal, FaBuilding, FaRecycle, FaTruck, FaTag, FaTimes, FaHandHoldingHeart, FaStore } from 'react-icons/fa';
import './Subscribe.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const PLANS = [
  {
    id: 'authority',
    name: 'Public Authority',
    icon: FaBuilding,
    price: 299,
    period: 'month',
    features: [
      'Crew Management System',
      'Waste Assignment Pipeline',
      'Compliance & Ordinance Tracking',
      'Priority Scoring Dashboard',
      'SLA Analytics & Reports',
      'Issue Jobs to Private Companies',
      'Unlimited Team Members'
    ],
    color: '#3498db'
  },
  {
    id: 'private_company',
    name: 'Waste Management Company',
    icon: FaTruck,
    price: 199,
    period: 'month',
    features: [
      'Job Inbox & Acceptance',
      'Active Jobs Tracking',
      'Invoice Management',
      'Performance Metrics',
      'Cost Tracking & Reporting',
      'Priority Queue Access',
      'Up to 50 Team Members'
    ],
    color: '#e67e22'
  },
  {
    id: 'recycling',
    name: 'Recycling Business',
    icon: FaRecycle,
    price: 149,
    period: 'month',
    features: [
      'Recyclable Waste Filter',
      'Material Value Estimator',
      'Material Inventory Tracking',
      'Market Rate Updates',
      'Collection Route Planning',
      'Environmental Impact Reports',
      'Up to 25 Team Members'
    ],
    color: '#27ae60'
  },
  {
    id: 'food_bank',
    name: 'Food Banks & Community Fridges',
    icon: FaHandHoldingHeart,
    price: 99,
    period: 'month',
    features: [
      'Real-time food alerts by territory',
      'Quantity threshold notifications',
      'Food safety compliance tools',
      'Collection management',
      'Allergen tracking',
      'Expiry monitoring'
    ],
    color: '#e74c3c'
  },
  {
    id: 'charity_shop',
    name: 'Charity Shops',
    icon: FaStore,
    price: 99,
    period: 'month',
    features: [
      'Item condition filtering',
      'Postcode-based alerts',
      'Inventory management',
      'Donation tracking',
      'Quality assessment tools'
    ],
    color: '#9b59b6'
  }
];

const Subscribe = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [selectedPlan, setSelectedPlan] = useState(userProfile?.subscription?.type || 'authority');
  const [processing, setProcessing] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState({ demoMode: true });
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  const currentPlan = PLANS.find(p => p.id === selectedPlan);
  
  const calculateDiscountedPrice = () => {
    if (!appliedCoupon || !currentPlan) return currentPlan?.price || 0;
    const discount = (currentPlan.price * appliedCoupon.discountPercent) / 100;
    return currentPlan.price - discount;
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    
    setValidatingCoupon(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: couponCode.trim() })
      });
      
      const data = await response.json();
      
      if (data.valid) {
        setAppliedCoupon(data.coupon);
        toast.success(`Coupon applied! ${data.coupon.discountPercent}% off for ${data.coupon.durationMonths} months`);
      } else {
        toast.error(data.error || 'Invalid coupon code');
      }
    } catch (error) {
      toast.error('Failed to validate coupon');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.info('Coupon removed');
  };

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/payment/config`)
      .then(res => res.json())
      .then(config => setPaymentConfig(config))
      .catch(() => setPaymentConfig({ demoMode: true }));
  }, []);

  const handlePayment = async () => {
    setProcessing(true);
    
    try {
      const token = localStorage.getItem('authToken');
      
      if (paymentConfig.stripeEnabled && paymentMethod === 'stripe') {
        const response = await fetch(`${API_BASE_URL}/api/payment/create-checkout`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ planId: selectedPlan, paymentMethod: 'stripe' })
        });
        
        const data = await response.json();
        if (data.url) {
          window.location.href = data.url;
          return;
        }
      }

      const response = await fetch(`${API_BASE_URL}/api/payment/demo-activate`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ planId: selectedPlan, paymentMethod, couponCode: appliedCoupon?.code })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`${paymentMethod === 'stripe' ? 'Stripe' : 'PayPal'} payment processed successfully!`);
        toast.info('Your subscription is now active. Redirecting to dashboard...');
        
        setTimeout(() => {
          navigate('/corporate-dashboard');
        }, 2000);
      } else {
        toast.error(data.error || 'Payment failed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment processing failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="subscribe-page">
      <div className="subscribe-container">
        <div className="subscribe-header">
          <h1>Choose Your Plan</h1>
          <p>Select the subscription that best fits your organisation's needs</p>
        </div>

        <div className="plans-grid">
          {PLANS.map(plan => {
            const Icon = plan.icon;
            const isSelected = selectedPlan === plan.id;
            return (
              <div 
                key={plan.id}
                className={`plan-card ${isSelected ? 'selected' : ''}`}
                onClick={() => setSelectedPlan(plan.id)}
                style={{ '--plan-color': plan.color }}
              >
                <div className="plan-header">
                  <div className="plan-icon" style={{ background: plan.color }}>
                    <Icon />
                  </div>
                  <h3>{plan.name}</h3>
                </div>
                <div className="plan-price">
                  <span className="currency">£</span>
                  <span className="amount">{plan.price}</span>
                  <span className="period">/{plan.period}</span>
                </div>
                <ul className="plan-features">
                  {plan.features.map((feature, idx) => (
                    <li key={idx}>
                      <FaCheck className="check-icon" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                {isSelected && (
                  <div className="selected-badge">Selected</div>
                )}
              </div>
            );
          })}
        </div>

        <div className="payment-section">
          <h2>Payment Method</h2>
          <p className="payment-subtitle">Choose how you'd like to pay for your subscription</p>

          <div className="payment-toggle">
            <button 
              className={`payment-option ${paymentMethod === 'stripe' ? 'active' : ''}`}
              onClick={() => setPaymentMethod('stripe')}
            >
              <FaCreditCard className="payment-icon" />
              <div className="payment-info">
                <span className="payment-name">Credit/Debit Card</span>
                <span className="payment-provider">Powered by Stripe</span>
              </div>
            </button>
            <button 
              className={`payment-option ${paymentMethod === 'paypal' ? 'active' : ''}`}
              onClick={() => setPaymentMethod('paypal')}
            >
              <FaPaypal className="payment-icon paypal" />
              <div className="payment-info">
                <span className="payment-name">PayPal</span>
                <span className="payment-provider">Pay with your PayPal account</span>
              </div>
            </button>
          </div>

          <div className="coupon-section">
            <h3><FaTag /> Have a Coupon Code?</h3>
            {appliedCoupon ? (
              <div className="applied-coupon">
                <div className="coupon-details">
                  <span className="coupon-badge">{appliedCoupon.code}</span>
                  <span className="coupon-discount">{appliedCoupon.discountPercent}% off for {appliedCoupon.durationMonths} months</span>
                </div>
                <button className="remove-coupon-btn" onClick={removeCoupon}>
                  <FaTimes /> Remove
                </button>
              </div>
            ) : (
              <div className="coupon-input-group">
                <input
                  type="text"
                  placeholder="Enter coupon code"
                  value={couponCode}
                  onChange={e => setCouponCode(e.target.value.toUpperCase())}
                  disabled={validatingCoupon}
                />
                <button 
                  className="apply-coupon-btn" 
                  onClick={handleApplyCoupon}
                  disabled={validatingCoupon}
                >
                  {validatingCoupon ? 'Validating...' : 'Apply Coupon'}
                </button>
              </div>
            )}
          </div>

          <div className="payment-summary">
            <div className="summary-row">
              <span>Plan</span>
              <span>{currentPlan?.name}</span>
            </div>
            <div className="summary-row">
              <span>Billing</span>
              <span>Monthly</span>
            </div>
            <div className="summary-row">
              <span>Regular Price</span>
              <span>£{currentPlan?.price}/month</span>
            </div>
            {appliedCoupon && (
              <div className="summary-row discount">
                <span>Discount ({appliedCoupon.discountPercent}%)</span>
                <span>-£{((currentPlan?.price * appliedCoupon.discountPercent) / 100).toFixed(2)}</span>
              </div>
            )}
            <div className="summary-row total">
              <span>Total</span>
              <span>£{calculateDiscountedPrice().toFixed(2)}/month</span>
            </div>
            {appliedCoupon && (
              <div className="coupon-note">
                Discount applies for the first {appliedCoupon.durationMonths} months
              </div>
            )}
          </div>

          {paymentConfig.demoMode && (
            <div className="demo-notice">
              <p>Demo Mode: No actual charges will be made. Your subscription will be activated for testing.</p>
            </div>
          )}

          <button 
            className="btn-subscribe"
            onClick={handlePayment}
            disabled={processing}
          >
            {processing ? (
              <>Processing...</>
            ) : paymentMethod === 'stripe' ? (
              <>
                <FaCreditCard /> Pay with Card
              </>
            ) : (
              <>
                <FaPaypal /> Pay with PayPal
              </>
            )}
          </button>

          <p className="terms-notice">
            By subscribing, you agree to our Terms of Service and Privacy Policy.
            You can cancel your subscription at any time.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Subscribe;
